import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { NS } from '@/constants';

import { AddressModel, CartShippingModel } from '@/models';
import { BANNER_TYPE, CallToActionKind } from '@/types';

import { useAppDispatch, useAppSelector } from '@/redux';
import {
  selectCartContainsAvailableForDeliveryProducts,
  selectRelevantCartDetailsForCheckout, selectShippingPriceCSR
} from '@/redux/slices/cart.slice';
import {
  checkIfStepIsEligibleForTaxFreeBilling,
  selectLastOrder,
  selectOrderContainsAvailableForDeliveryProducts
} from '@/redux/slices/checkout.slice';
import { selectCurrencyIso, selectStoreId } from '@/redux/slices/root.slice';
import { redirectToAnotherStep } from '@/redux/thunks/checkout/redirectToAnotherStep';

import { getCountryLabel } from '@/utils/addressUtils';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { dateToStringFormat, formatSlotToLocaleString } from '@/utils/dateUtils';

import Banner from '@/components/common/Banner';
import Divider from '@/components/common/Divider';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import WarningInformation from '@/components/common/WarningInformation';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';

import styles from './ReviewSection.module.scss';
import { COUNTRIES } from '@/constants/profile';
import { STORE_ID } from '@/constants/stores';

type StandardizedAddress = (model: CartShippingModel[] | AddressModel, isBillingAddress?: boolean) => {
  title: string;
  id: string;
  details: string[];
  action: () => void;
  isShippingMode: false;
};

type StandardizedShipping = (shipping: CartShippingModel[] | AddressModel, isBillingAddress?: boolean) => {
  title: string;
  id: string;
  details: { title: string; desc: string; price: string }[];
  action: () => void;
  isShippingMode: true;
}

type StandardizedModel = StandardizedAddress | StandardizedShipping;

export const ReviewSection = ({ readOnly = false }) => {
  const { t } = useTranslation(NS.CHECKOUT);
  const dispatch = useAppDispatch();
  const currencyIso = useAppSelector(selectCurrencyIso);
  const {
    shippingAddress, billingAddress, shippingMode, shouldBillAsTaxFree, taxRate, prearrivalShippingMode, primeurShippingMode
  } = useAppSelector(selectRelevantCartDetailsForCheckout);
  const shippingPriceCSR = useAppSelector(selectShippingPriceCSR);
  const isStepEligibleForTaxFreeBilling = useAppSelector(checkIfStepIsEligibleForTaxFreeBilling);
  const lastOrder = useAppSelector(selectLastOrder);
  const storeId = useAppSelector(selectStoreId);
  const locale = useAppSelector(({ root }) => root.storeConf.locale);
  const codeIso = useAppSelector(({ root }) => root.storeConf.codeISO);

  const checkIfLastOrderContainsOtherProductsThanPrimeur = () => {
    return function useCheck() {
      return useAppSelector(selectOrderContainsAvailableForDeliveryProducts);
    }();
  };

  const checkIfCartContainsOtherProductsThanPrimeur = () => {
    return function useCheck() {
      return useAppSelector(selectCartContainsAvailableForDeliveryProducts);
    }();
  };

  const isDesktop = useScreenSizeContext().isDesktop;

  const dateFormat = useAppSelector(({ root }) => root.storeConf.dateFormat);

  const finalShippingAddress = readOnly ? lastOrder.shipping : shippingAddress as AddressModel;
  const finalBillingAddress = readOnly ? lastOrder.billing : billingAddress as AddressModel;
  const finalShippingMode = readOnly ? lastOrder.shippingMode : shippingMode as CartShippingModel;
  const finalPrearrivalShippingMode = readOnly ? lastOrder.prearrivalShippingMode : prearrivalShippingMode as CartShippingModel;
  const finalPrimeurShippingMode = readOnly ? lastOrder.primeurShippingMode : primeurShippingMode as CartShippingModel;

  const standardizedShippingModes : StandardizedModel = (shippingModes: CartShippingModel[]) => {
    const shippingModesDetails = shippingModes.flatMap((shippingMode) => {
      const isOffered = shippingMode.isOffered || shippingMode.isDrive;
      const finalPrice =
        shippingPriceCSR === 0 ? shippingPriceCSR :
          isStepEligibleForTaxFreeBilling && shouldBillAsTaxFree ?
            (shippingMode.price / (1 + (taxRate / 100))) : shippingMode.price;

      const formatedPrice = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyIso
      }).format(finalPrice);

      return (
        [{
          title: shippingMode.title, desc: shippingMode.desc,
          price: finalContentContainsOtherProductsThanPrimeur && (isOffered ? t('common:product.free') : formatedPrice)
        }].filter(Boolean));
    });
    return {
      title: t('shipMode.title'),
      id: finalShippingMode?._id,
      details: shippingModesDetails,
      action: () => dispatch(redirectToAnotherStep('shipping')),
      isShippingMode: true
    };
  };

  const standardizedAddresses: StandardizedModel = (model: AddressModel, isBillingAddress: boolean) => {
    return {
      title: isBillingAddress ? t('billingAddress.title') : t('shippingAddress.title'),
      id: model?.id,
      details: [
            `${model.firstName}, ${model.lastName}`, model.company, model.street,
            `${model.postalCode}, ${model.city}`, getCountryLabel(model.country), model.phoneNumberMobile
      ].filter(Boolean),
      action: () => dispatch(redirectToAnotherStep(isBillingAddress ? 'billing' : 'address')),
      isShippingMode: false
    };
  };

  const finalContentContainsOtherProductsThanPrimeur = readOnly ?
    checkIfLastOrderContainsOtherProductsThanPrimeur() : checkIfCartContainsOtherProductsThanPrimeur();

  const delayedDeliveryDate = useMemo(() => {
    if (finalShippingMode?.selectedDeliveryDateDelayed) {
      return dateToStringFormat(finalShippingMode.selectedDeliveryDateDelayed, dateFormat);
    }
  }, [finalShippingMode?.selectedDeliveryDateDelayed]);

  const topChronoDate = useMemo(() => {
    if (finalShippingMode?.bookingSlot) {
      const slot = [finalShippingMode.bookingSlot?.start, finalShippingMode.bookingSlot?.end];
      const formatedDate = formatSlotToLocaleString(slot, locale);
      return `${t('checkout:shipMode.topChrono.deliverySlot')} ${formatedDate.day?.replaceAll('-', '/')} ${formatedDate.slot?.start} - ${formatedDate.slot?.end}`;
    }
  }, [finalShippingMode?.bookingSlot]);

  const computeShippingInfos = () => {
    const infosList: JSX.Element[] = [];

    if (finalShippingMode?.selectedDeliveryDateDelayed) {
      infosList.push(
        <div className={styles['delayed-delivery']}>
          {t('checkout:shipMode.summaryShippingFrom')}
          <span>
            {delayedDeliveryDate}
          </span>
          <MillesimaPrice value={finalShippingMode?.delayedShipping?.price} className={{ container: styles['price-bold'] }} />
        </div>
      );
    }

    if (finalShippingMode?.canBeBlocked && finalShippingMode.weatherHoldChecked) {
      infosList.push(
        <div className={styles['delayed-delivery']}>
          <div dangerouslySetInnerHTML={{ __html: finalShippingMode.weatherHold?.wordingReprise }} />
          <MillesimaPrice value={finalShippingMode?.weatherHold?.price} className={{ container: styles['price-bold'] }} />
        </div>
      );
    }

    if (finalShippingMode?.pickUpLater) {
      infosList.push(
        <div className={styles.timeslot}>
          {t('checkout:shipMode.contactNeeded')}
        </div>
      );
    }

    if ((finalShippingMode?.isTopChrono || finalShippingMode?.isSchedulable) && finalShippingMode.bookingSlot) {
      infosList.push(
        <div className={styles['delayed-delivery']}>
          <Banner
            type={BANNER_TYPE.INFORMATION}
            content={topChronoDate}
            showLeading={false}
          />
        </div>
      );
    }

    return infosList;
  };

  const differentCountries = (() => {
    let different = finalShippingAddress.country !== codeIso;
    if (different &&
      ((finalShippingAddress.country === COUNTRIES.MONACO && codeIso === COUNTRIES.FRANCE) ||
        storeId === STORE_ID.COM)) {
      different = false;
    }
    return different;
  })();

  const isNorthernIreland = (() => {
    return codeIso === COUNTRIES.UNITED_KINGDOM && finalShippingAddress.postalCode?.startsWith('BT');
  })();

  const standardizedInfos = [
    standardizedAddresses(finalShippingAddress, false), standardizedAddresses(finalBillingAddress, true),
    standardizedShippingModes(
      [finalShippingMode, finalPrearrivalShippingMode, finalPrimeurShippingMode].filter(Boolean)
    )
  ].filter(Boolean);

  const buttonShouldShow = (info: ReturnType<StandardizedModel>) => (
    !readOnly && (
      !info.isShippingMode || (info.isShippingMode && finalContentContainsOtherProductsThanPrimeur)
    )
  );

  return (
    <>
      {isNorthernIreland ? (
        <WarningInformation title={t('checkout:shippingAddress.foreignDeliveryTitle')}>
          <p>{t('checkout:shippingAddress.foreignDeliveryIreland')}</p>
        </WarningInformation>
      ) : (
        differentCountries && (
          <WarningInformation title={t('checkout:shippingAddress.foreignDeliveryTitle')}>
            <p>{t('checkout:shippingAddress.foreignDeliveryEN')}</p>
            <p>{t('checkout:shippingAddress.foreignDelivery')}</p>
          </WarningInformation>
        )
      )}
      <div className={styles['card-container']}>
        {standardizedInfos.map((info, index) => {
          const isLast = index === standardizedInfos.length - 1;
          return (
            <section key={`${1}-${index}`} className={styles['info-container']}>
              <p className={styles.title}>{info.title}</p>
              <section className={styles.details}>
                {info.details.map((detail: ReturnType<StandardizedModel>['details'][number], index: number) => {
                  if (info.isShippingMode) {
                    const shippingDetails = detail as ReturnType<StandardizedShipping>['details'][number];
                    const shipmentNumber = info.details.length > 1 ? t('checkout:cart.shipment') : null;
                    const isLast = index === info.details.length - 1;

                    const classNames = [
                      shippingDetails.price === t('common:product.free') ? styles.free : styles.detail,
                      !isLast && styles.spacer
                    ].filter(Boolean).join(' ');

                    return (
                      <>
                        {shipmentNumber && (
                          <p className={styles.shipment}>
                            {`${shipmentNumber} ${index + 1}/${info.details.length}`}
                          </p>
                        )}
                        <p className={styles.detail}>
                          {shippingDetails.title}
                        </p>
                        <p className={styles.detail}>
                          {shippingDetails.desc}
                        </p>
                        <p className={classNames}>
                          {shippingDetails.price}
                        </p>
                        <div className={styles['additional-shipping-infos']}>
                          {computeShippingInfos()}
                        </div>
                      </>
                    );
                  }
                  return <p key={detail as string}
                    className={detail === t('common:product.free') ? styles.free : styles.detail}>{detail as string}</p>;
                })}
              </section>
              {buttonShouldShow(info) && (
                <CallToActionContainer
                  fullWidth={false} fullWidthOnMobile={false} kind={CallToActionKind.GHOST} className={styles.edit}
                  buttonSize="small" onClick={info.action}
                >
                  {t('common:btn.modify')}
                </CallToActionContainer>
              )}
              {!isLast && (
                <div className={styles.divider}>
                  <Divider spacing={24} orientation={isDesktop ? 'vertical' : 'horizontal'}/>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
};
